import { LoginStatus } from "../generated/graphql";
import { CRM_RECENT_RECORDS_KEY } from "./crm";

export type AuthSurface = "admin" | "learning";

export type AuthMethod = "company";

export type AuthData = {
  token: string;
  status: LoginStatus;
  email: string; // Used only to validate the old password when changing it.
  surface: AuthSurface;
  /** Company sessions have no portal password. */
  method?: AuthMethod;
};

const LEGACY_AUTH_KEY = "auth";
const ACTIVE_SURFACE_KEY = "auth.activeSurface";

const authKey = (surface: AuthSurface): string => `auth.${surface}`;

const isAuthSurface = (value: unknown): value is AuthSurface => value === "admin" || value === "learning";

const isLoginStatus = (value: unknown): value is LoginStatus =>
  typeof value === "string" && Object.values(LoginStatus).includes(value as LoginStatus);

const parseAuthData = (value: string | null, surface: AuthSurface): AuthData | null => {
  if (!value) return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("token" in parsed) ||
      typeof parsed.token !== "string" ||
      parsed.token.length === 0 ||
      !("email" in parsed) ||
      typeof parsed.email !== "string" ||
      !("status" in parsed) ||
      !isLoginStatus(parsed.status) ||
      ("surface" in parsed && parsed.surface !== surface)
    ) {
      return null;
    }

    return {
      token: parsed.token,
      email: parsed.email,
      status: parsed.status,
      surface,
      ...("method" in parsed && parsed.method === "company" ? { method: "company" as const } : {}),
    };
  } catch {
    return null;
  }
};

const migrateLegacyAdminSession = (): void => {
  const legacyValue = localStorage.getItem(LEGACY_AUTH_KEY);
  if (!legacyValue) return;

  if (!localStorage.getItem(authKey("admin"))) {
    const legacyAuth = parseAuthData(legacyValue, "admin");
    if (legacyAuth) {
      localStorage.setItem(authKey("admin"), JSON.stringify(legacyAuth));
      localStorage.setItem(ACTIVE_SURFACE_KEY, "admin");
    }
  }

  localStorage.removeItem(LEGACY_AUTH_KEY);
};

export const getActiveAuthSurface = (): AuthSurface | null => {
  migrateLegacyAdminSession();
  const surface = localStorage.getItem(ACTIVE_SURFACE_KEY);
  if (surface === null) return null;
  if (isAuthSurface(surface)) return surface;
  clearAuthData();
  return null;
};

export const getAuthData = (surface = getActiveAuthSurface()): AuthData | null => {
  if (!surface) return null;
  const auth = parseAuthData(localStorage.getItem(authKey(surface)), surface);
  if (auth) return auth;

  clearAuthData(surface);
  return null;
};

export const storeAuthData = (authData: AuthData): void => {
  const inactiveSurface: AuthSurface = authData.surface === "admin" ? "learning" : "admin";
  localStorage.removeItem(authKey(inactiveSurface));
  localStorage.removeItem(LEGACY_AUTH_KEY);
  localStorage.setItem(authKey(authData.surface), JSON.stringify(authData));
  localStorage.setItem(ACTIVE_SURFACE_KEY, authData.surface);
};

export const clearAuthData = (surface?: AuthSurface): void => {
  if (surface) {
    localStorage.removeItem(authKey(surface));
    if (localStorage.getItem(ACTIVE_SURFACE_KEY) === surface) {
      localStorage.removeItem(ACTIVE_SURFACE_KEY);
    }
    return;
  }

  localStorage.removeItem(authKey("admin"));
  localStorage.removeItem(authKey("learning"));
  localStorage.removeItem(ACTIVE_SURFACE_KEY);
  localStorage.removeItem(LEGACY_AUTH_KEY);
  // The records the previous user opened are not for the next one.
  localStorage.removeItem(CRM_RECENT_RECORDS_KEY);
};
